import useConfig from "../../../../actions/Tenants/config/configHook";
import DefectGroupConfigForm from "./DefectGroupConfigForm";
import FieldConfigForm from "./FieldConfigForm";
import FilterableConfigList from "./FilterableConfigList";
import { yesOrNoRadio } from "./util";

export enum PDFPageTemplate {
    two_col_layer_meta = "two_col_layer_meta",
    one_col_layer_meta = "one_col_layer_meta",
    pallets_table = "pallets_table",
    check_summary_fruit_table = "check_summary_fruit_table",
    // check_images = "check_images",
    // advance_ripening = "advance_ripening",
}

// How a section turns the layers/checks it targets into rendered pages.
// The list of layers is derived from the checks of the section's check_types
// (a check is always done on a specific layer), so no explicit scope is needed.
export enum MergeStrategy {
    // One page per layer, all preferred checks merged into one (default behaviour).
    merge_all_latest = "merge_all_latest",
    // One page per layer, only the single most-recent check.
    latest_only = "latest_only",
    // One page per layer per day, checks merged within each day.
    merge_per_day = "merge_per_day",
    // One page per individual check.
    one_per_check = "one_per_check",
    // Render the parent layer page once per fruit_type found in the hierarchy.
    parent_per_fruit_type = "parent_per_fruit_type",
}

const mergeStrategies = [
    { label: "(grand)children → merge all latest checks per layer (default)", value: MergeStrategy.merge_all_latest },
    { label: "(grand)children → only the latest check per layer", value: MergeStrategy.latest_only },
    { label: "(grand)children → merge per day, one page per day", value: MergeStrategy.merge_per_day },
    { label: "(grand)children → one page per check", value: MergeStrategy.one_per_check },
    { label: "parent → one page per fruit_type in the hierarchy", value: MergeStrategy.parent_per_fruit_type },
];

const pageTemplates = [
    { label: "2 Columns with Layer and check meta + optional images", value: PDFPageTemplate.two_col_layer_meta },
    // TODO: this could also be one field with two_col_layer_meta
    { label: "1 Column with layer and check meta + optional images", value: PDFPageTemplate.one_col_layer_meta },
    { label: "Pallets table (all children pallets)", value: PDFPageTemplate.pallets_table },
    // is it layer meta + check meta or only check meta?
    { label: "Check summary + defects frequency + fruit table + images", value: PDFPageTemplate.check_summary_fruit_table },
    // { label: "Advance ripening", value: PDFPageTemplate.advance_ripening },
];

const defaultField = {
    field: {
        page_template: PDFPageTemplate.two_col_layer_meta,
    }
};

interface PDFDynamicSectionsConfigFormProps {
    title: string;
    fields: any[];
    name: string;
    description: string;
    useDisplayFilter?: boolean | string[];
    setArray: (fields: object[]) => null;
}

// TODO can we make this component reusable?
export const PDFDynamicSectionsConfigForm = ({
    name,
    title,
    fields,
    description,
    setArray,
    useDisplayFilter = false,
}: PDFDynamicSectionsConfigFormProps) => {
    const config = useConfig();

    const getFieldTitle = (object) => {
        return (
            <span>
                <b>{pageTemplates.find((pt) => pt.value === object.page_template)?.label ?? "PDF Section"}</b>
                {!!object?.check_types && " - "}
                {object?.check_type}
            </span>
        );
    };

    const conditionalDisplayFields = {
        defect_groups: [
            {
                field: "page_template",
                value: [PDFPageTemplate.two_col_layer_meta, PDFPageTemplate.one_col_layer_meta, PDFPageTemplate.check_summary_fruit_table]
            },
            {
                field: "additional_details",
                value: ["defects"]
            },
        ],
        primary_fields: [
            {
                field: "page_template",
                value: [PDFPageTemplate.two_col_layer_meta, PDFPageTemplate.one_col_layer_meta, PDFPageTemplate.check_summary_fruit_table, PDFPageTemplate.pallets_table]
            },
        ],
        secondary_fields: [
            {
                field: "page_template",
                value: [PDFPageTemplate.two_col_layer_meta]
            },
        ],
        additional_details: [
            {
                field: "page_template",
                value: [PDFPageTemplate.two_col_layer_meta]
            },
        ],
        merge_strategy: [
            {
                field: "page_template",
                value: [
                    PDFPageTemplate.two_col_layer_meta,
                    PDFPageTemplate.one_col_layer_meta,
                    PDFPageTemplate.check_summary_fruit_table,
                ]
            },
        ],
        link_pallet_labels: [
            {
                field: "page_template",
                value: [PDFPageTemplate.pallets_table]
            },
        ]
    };

    const getFieldForm = (object, setObject) => {
        return [
            {
                label: "Title",
                description: "Title of the component, leave blank for default title.",
                type: "text",
                name: "title",
            },
            {
                label: "Page template",
                description: "The page template to use for this content.",
                name: "page_template",
                type: "single-select",
                options: pageTemplates,
            },
            {
                label: "Check types",
                description: "What check types to include in this section of the report. Will be used to query fruit and images and latest check info.",
                name: "check_types",
                type: "single-select",
                return_array: true,
                multi: true,
                options: config.check_locations.map(({ value, text }) => ({ value, label: `${text} (${value})` }))
            },
            {
                label: "Fallback check types",
                description: "Fallback check types to use when no check is found for the selected check types.",
                name: "fallback_check_types",
                type: "single-select",
                multi: true,
                return_array: true,
                options: config.check_locations.map(({ value, text }) => ({ value, label: `${text} (${value})` }))
            },
            {
                type: "element",
                name: "primary_fields",
                label: "Primary fields",
                description: "What fields to display in the primary section.",
                el: <FieldConfigForm
                    fields={object?.primary_fields || []}
                    description="What fields to display in the top / left section."
                    name="primary_fields"
                    title="Primary fields"
                    setArray={(primary_fields) => setObject({ ...object, primary_fields })} />
            },
            {
                type: "element",
                name: "secondary_fields",
                label: "Secondary fields",
                description: " What fields to display in the secondary section.",
                el: <FieldConfigForm
                    fields={object?.secondary_fields || []}
                    description="What fields to display in the bottom / right section."
                    name="secondary_fields"
                    title="Secondary fields"
                    setArray={(secondary_fields) => setObject({ ...object, secondary_fields })}
                />
            },
            {
                label: "Defect groups",
                description: "What group of defects do you want to show at the header of summary and pdf page?",
                name: "defect_groups",
                type: "element",
                el: <DefectGroupConfigForm
                    fields={object?.defect_groups || []}
                    description="What group of defects do you want to show at the header of summary and pdf page?"
                    name="defect_groups"
                    title="Defect groups"
                    setArray={(defect_groups) => setObject({ ...object, defect_groups })} />
            },
            {
                label: "Additional details",
                description: "Additional details/checks to include in this section of the report.",
                name: "additional_details",
                type: "single-select",
                multi: true,
                options: [
                    { value: "dry_matter_min_max", label: "Dry matter min/max" },
                    { value: "scoring_overview", label: "Pallet scoring overview" },
                    { value: "general_status", label: "Overall decision" },
                    { value: "images", label: "Images" },
                    { value: "defects", label: "Defect groups" },
                ],
            },
            {
                label: "Only checks for this layer",
                name: "only_checks_for_this_layer",
                description: "Only use checks done for this layer (e.g. this sales order), not for another parent of the same pallet. Applies to the pages and the pallet table of this section. Fallback checks are not filtered. Checks created before parents were stored on checks still count for every parent.",
                ...yesOrNoRadio
            },
            {
                label: "Merge strategy",
                description: "How to turn the layers/checks of this section into pages. The layers are derived from the checks of the selected check types.",
                name: "merge_strategy",
                type: "single-select",
                options: mergeStrategies,
            },
            {
                label: "Link pallet labels to pallet pages",
                description: "Make the pallet labels in this table clickable, jumping to each pallet's first page further down in the report. Pallets without a page in the report stay plain text.",
                name: "link_pallet_labels",
                ...yesOrNoRadio
            },
        ]
            .filter((field) => !conditionalDisplayFields[field.name]
                || conditionalDisplayFields[field.name]?.some((condition) => {
                    const fieldValue = (object ?? {})[condition.field];
                    if (Array.isArray(fieldValue)) {
                        return fieldValue.some((v) => condition.value.includes(v));
                    }
                    return !!fieldValue && condition.value.includes(fieldValue);
                })) as any;
    };

    return (
        <FilterableConfigList
            useDisplayFilter={useDisplayFilter as false | string[]}
            name={name}
            title={title}
            description={description}
            fields={fields}
            setArray={setArray}
            defaultField={defaultField}
            getFieldForm={getFieldForm}
            getFieldTitle={getFieldTitle}
        />
    );
};
